import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { motion } from 'framer-motion';
import { Lock, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lazy load tab components to separate chunks
const HeroTab = lazy(() => import('../components/admin/tabs/HeroTab'));
const AboutTab = lazy(() => import('../components/admin/tabs/AboutTab'));
const ContactTab = lazy(() => import('../components/admin/tabs/ContactTab'));
const StatsTab = lazy(() => import('../components/admin/tabs/StatsTab'));
const ServicesTab = lazy(() => import('../components/admin/tabs/ServicesTab'));
const NewsTab = lazy(() => import('../components/admin/tabs/NewsTab'));
const BoatsTab = lazy(() => import('../components/admin/tabs/BoatsTab'));
const EventsTab = lazy(() => import('../components/admin/tabs/EventsTab'));
const PartnersTab = lazy(() => import('../components/admin/tabs/PartnersTab'));
const PageHeroesTab = lazy(() => import('../components/admin/tabs/PageHeroesTab'));
const ImagesTab = lazy(() => import('../components/admin/tabs/ImagesTab'));
const InstagramTab = lazy(() => import('../components/admin/tabs/InstagramTab'));
const GalleryTab = lazy(() => import('../components/admin/tabs/GalleryTab'));
const EmailTab = lazy(() => import('../components/admin/tabs/EmailTab'));
const SeoTab = lazy(() => import('../components/admin/tabs/SeoTab'));
const BackupTab = lazy(() => import('../components/admin/tabs/BackupTab'));
const MessagesTab = lazy(() => import('../components/admin/tabs/MessagesTab'));
const SecurityTab = lazy(() => import('../components/admin/tabs/SecurityTab'));

const tabs = [
  { id: 'messages', label: 'Messaggi', Component: MessagesTab },
  { id: 'hero', label: 'Hero', Component: HeroTab },
  { id: 'about', label: 'Chi Siamo', Component: AboutTab },
  { id: 'contact', label: 'Contatti', Component: ContactTab },
  { id: 'stats', label: 'Statistiche', Component: StatsTab },
  { id: 'services', label: 'Servizi', Component: ServicesTab },
  { id: 'news', label: 'News', Component: NewsTab },
  { id: 'boats', label: 'Barche', Component: BoatsTab },
  { id: 'events', label: 'Eventi', Component: EventsTab },
  { id: 'partners', label: 'Partner', Component: PartnersTab },
  { id: 'gallery', label: 'Galleria', Component: GalleryTab },
  { id: 'pageheroes', label: 'Sfondi', Component: PageHeroesTab },
  { id: 'images', label: 'Immagini', Component: ImagesTab },
  { id: 'instagram', label: 'Instagram', Component: InstagramTab },
  { id: 'email', label: 'Email', Component: EmailTab },
  { id: 'seo', label: 'SEO', Component: SeoTab },
  { id: 'security', label: 'Sicurezza', Component: SecurityTab },
  { id: 'backup', label: 'Backup', Component: BackupTab },
];

const TabSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="animate-spin w-6 h-6 border-2 border-[#0B1221] border-t-transparent rounded-full" />
  </div>
);

export const AdminPage = () => {
  const { isAuthenticated, loading, login, logout, getAuthHeaders, token } = useAdmin();
  const navigate = useNavigate();

  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('messages');
  const [unreadBadge, setUnreadBadge] = useState(0);

  // Fetch unread message + pending quote count for badge
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const fetchBadge = async () => {
      try {
        const headers = getAuthHeaders();
        const [msgRes, quoteRes] = await Promise.all([
          axios.get(`${API}/admin/messages`, { headers }),
          axios.get(`${API}/admin/quote-requests`, { headers })
        ]);
        const unreadMsgs = (msgRes.data || []).filter(m => !m.read).length;
        const pendingQuotes = (quoteRes.data || []).filter(q => !q.status || q.status === 'pending').length;
        setUnreadBadge(unreadMsgs + pendingQuotes);
      } catch (e) { /* silently ignore */ }
    };
    fetchBadge();
  }, [isAuthenticated, token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const result = await login(loginForm.username, loginForm.password);
      if (result.success) {
        toast.success('Login effettuato');
      } else {
        toast.error(result.error || 'Credenziali non valide');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0B1221] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center bg-[#F5F5F7] pt-20 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md p-6 sm:p-8 bg-white shadow-lg"
        >
          <div className="text-center mb-8">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#0B1221] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#0B1221]">Admin Login</h1>
            <p className="text-[#6E6E73] mt-2 text-sm sm:text-base">Accedi per gestire il sito</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input data-testid="admin-username" placeholder="Username" value={loginForm.username} onChange={(e) => setLoginForm({...loginForm, username: e.target.value})} required className="bg-[#F5F5F7] border-none py-4" />
            <Input data-testid="admin-password" type="password" placeholder="Password" value={loginForm.password} onChange={(e) => setLoginForm({...loginForm, password: e.target.value})} required className="bg-[#F5F5F7] border-none py-4" />
            <Button data-testid="admin-login-btn" type="submit" disabled={loginLoading} className="w-full bg-[#0B1221] text-white hover:bg-[#1A2639] rounded-full py-4">
              {loginLoading ? '...' : 'Accedi'}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  const activeTabData = tabs.find(t => t.id === activeTab);
  const ActiveComponent = activeTabData ? activeTabData.Component : null;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F5F5F7] pt-20 sm:pt-24 pb-24 sm:pb-12">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0B1221]">Pannello Admin</h1>
            <p className="text-[#6E6E73] text-sm sm:text-base">Gestisci i contenuti del sito</p>
          </div>
          <Button data-testid="admin-logout-btn" onClick={handleLogout} variant="outline" className="rounded-full text-sm">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Esci</span>
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                data-testid={`admin-tab-${tab.id}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'messages') setUnreadBadge(0);
                }}
                className={`relative px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#0B1221] text-white'
                    : 'bg-white text-[#6E6E73] border border-[#E5E5EA] hover:border-[#0B1221] hover:text-[#0B1221]'
                }`}
              >
                {tab.label}
                {tab.id === 'messages' && unreadBadge > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadBadge > 99 ? '99+' : unreadBadge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <Suspense fallback={<TabSpinner />}>
          {ActiveComponent && (
            <ActiveComponent getAuthHeaders={getAuthHeaders} token={token} />
          )}
        </Suspense>
      </div>
    </div>
  );
};
